import { Link, MenuItem, SvgIcon, Tooltip, Typography } from '@mui/material';
import { ChevronRight as ChevronRightIcon } from '@opengov/react-capital-assets';
import React from 'react';

import { CounterAdornment } from '../Components/CounterAdornment';
import { tooltipOffsetProps } from '../Components/styleConstants';
import { handleTabOrEscapeToLeaveSubmenu } from '../utils/keyboardNavUtils';

import { DATA_OG, DATA_TEST, getDynamicDataOGId, getDynamicDataTestId } from '../enums';
import { IMenuOption } from '../types';
import { useCombinedRefs } from '../utils/hooks/useCombinedRefs';

const styles = {
  menuItem: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 1,
    paddingLeft: 2,
    color: 'text.primary'
  },
  link: {
    width: '100%',
    border: 'none',
    textAlign: 'inherit',
    backgroundColor: 'inherit',
    color: 'inherit',
    ':visited': {
      color: 'text.primary'
    }
  },
  text: {
    display: 'flex'
  }
};

interface IMoreMenuItemProps {
  item: IMenuOption;
  index: number;
  firstMoreMenuItemRef: React.MutableRefObject<HTMLLIElement>;
  anchorRef: React.MutableRefObject<HTMLLIElement>;
  isLeafNode: boolean;
  handleClick(): void;
}

export const MoreMenuItem: React.FC<IMoreMenuItemProps> = ({
  item,
  index,
  firstMoreMenuItemRef,
  anchorRef,
  isLeafNode,
  handleClick
}) => {
  const labelRef = React.useRef<HTMLAnchorElement>(null);
  const menuItemRef = React.useRef<HTMLLIElement>(null);
  const combinedRef = useCombinedRefs(index === 0 ? firstMoreMenuItemRef : null, menuItemRef);
  return (
    <Tooltip
      key={item.id}
      data-test={DATA_TEST.more_menu_tooltip}
      title={item?.tooltipText || ''}
      placement="top-start"
      enterNextDelay={500}
      disableInteractive={true}
      slotProps={{
        popper: {
          // Ensures that a warning does not appear in console in certain cases when menu item is removed from the DOM
          // Fixes: https://opengovinc.atlassian.net/browse/PSS-1446
          // Code based on: https://mui.com/material-ui/react-tooltip/#virtual-element
          anchorEl: {
            getBoundingClientRect: () => {
              const rect = menuItemRef.current!.getBoundingClientRect();
              return new DOMRect(rect.x, rect.y);
            }
          }
        }
      }}
      {...tooltipOffsetProps}
    >
      <MenuItem
        data-og={getDynamicDataOGId(DATA_OG.menu_item, item.label)}
        data-test={getDynamicDataTestId(DATA_TEST.more_menu_item, item.id)}
        sx={styles.menuItem}
        autoFocus={index === 0}
        ref={combinedRef}
        onKeyDown={(e) => {
          handleTabOrEscapeToLeaveSubmenu(e.key, anchorRef.current);
          if ([' ', 'Enter'].includes(e.key)) {
            if (item.url && labelRef?.current) {
              e.preventDefault();
              // If item has a URL, click inner Link component
              // (important for keyboard navigation & general accessibility)
              labelRef?.current?.click();
            }
          }
          if (e.key === 'ArrowRight' && !isLeafNode) {
            e.preventDefault();
            handleClick();
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          handleClick();
          if (item.url) {
            // If we didn't click directly on the url, click inner Link component
            if (e.target !== labelRef?.current) {
              e.preventDefault();
              labelRef?.current?.click();
            }
            return;
          }
        }}
      >
        {item.url ? (
          <Link
            variant="h5"
            href={item.url}
            target={item.url && item.openInNewTab ? '_blank' : undefined}
            ref={labelRef}
            underline="none"
            sx={{ ...styles.link, ...styles.text }}
            tabIndex={-1}
            component={item.linkComponent || Link}
          >
            {item.label}
            {item.counterAdornmentValue && item.counterAdornmentValue >= 0 && (
              <CounterAdornment count={item.counterAdornmentValue} />
            )}
          </Link>
        ) : (
          <Typography variant="h5" component="span" sx={styles.text}>
            {item.label}
            {item.counterAdornmentValue && item.counterAdornmentValue >= 0 && (
              <CounterAdornment count={item.counterAdornmentValue} />
            )}
          </Typography>
        )}
        {!isLeafNode && <SvgIcon inheritViewBox component={ChevronRightIcon} />}
      </MenuItem>
    </Tooltip>
  );
};
